package pl.decks.personal.components.layouts.blog

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaCalendar
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobwebx.markdown.markdown
import pl.decks.personal.components.layouts.blog.BlogPostStylesheet.Front
import pl.decks.personal.components.layouts.site.SiteLayoutData
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet
import pl.decks.personal.components.widgets.tag.HashTag
import pl.decks.personal.components.widgets.tag.TagsList
import pl.decks.personal.styles.SiteLayout

@InitRoute
fun initBlogPostLayout(ctx: InitRouteContext) {
    val blogPostData = BlogPostData.fromFrontMatter(ctx.markdown!!.frontMatter)

    ctx.data.add(SiteLayoutData(blogPostData.title, blogPostData.description))
    ctx.data.add(blogPostData)
}

@Composable
@Layout(".components.layouts.site.SiteLayout")
fun BlogPostLayout(content: @Composable () -> Unit) {
    val ctx = rememberPageContext()
    val blogPostData = ctx.data.getValue<BlogPostData>()
    Column(
        modifier = BlogPostStylesheet.Layout.toModifier(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        BlogPostHeroHeader(blogPostData)
        Column(SiteLayoutStylesheet.Page.Content.toModifier().then(BlogPostStylesheet.Markdown.toModifier())) {
            content()
        }
    }
}

@Composable
private fun BlogPostHeroHeader(blogPostData: BlogPostData) = Column(
    modifier = Front.Header.toModifier(),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    blogPostData.heroImage?.let { BlogPostHeroImage(it) }
    BlogPostHeroDetails(blogPostData)
    HorizontalDivider(Front.Divider.toModifier())
}

@Composable
private fun BlogPostHeroImage(imageSrc: String) = Box(Front.ImageContainer.toModifier(), contentAlignment = Alignment.Center) {
    Image(imageSrc, modifier = Front.Image.toModifier())
}

@Composable
private fun BlogPostHeroDetails(
    blogPostData: BlogPostData,
) = Column(Front.Details.toModifier()) {
    BlogPostHeroAuthorship(blogPostData.date, blogPostData.author)
    BlogPostHeroTitle(blogPostData.title)
    blogPostData.description?.let { BlogPostHeroDescription(it) }
    BlogPostHeroTags(blogPostData.tags)
}

@Composable
private fun BlogPostHeroAuthorship(date: String?, author: String) = Row(
    modifier = Front.Authorship.toModifier(),
    horizontalArrangement = Arrangement.SpaceBetween,
) {
    date?.let {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(SiteLayout.Space.XXSmall)
        ) {
            FaCalendar()
            SpanText(it)
        }
    }
    Spacer()
    SpanText("by: $author")
}

@Composable
private fun BlogPostHeroTitle(title: String) = SpanText(
    title,
    modifier = Front.Title.toModifier(),
)

@Composable
private fun BlogPostHeroDescription(description: String) = SpanText(description)

@Composable
private fun BlogPostHeroTags(tags: List<String>) = TagsList(
    modifier = Modifier.fillMaxWidth(),
) {
    tags.forEach { tag -> HashTag(tag) }
}
